import { HiMenu, HiMinus, HiOutlineLightBulb, HiPencilAlt } from "react-icons/hi";
import { MdFullscreen } from "react-icons/md";
import { css } from "../../../styled-system/css";
import { useContext, useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { styled, VStack } from "../../../styled-system/jsx";
import { useFetch } from "../../hooks/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../utils/api";
import { useAppSelector } from "../../redux/hooks";
import Markdown from "react-markdown";
// import Loading from "./Loading";
import { ChatContext } from "./ChatContext";
import TrellisLink from "../core/basic/TrellisLink";
import FullScreenChat from "./FullScreenChat";
import ConversationsSidebar from "./ConversationsSidebar";
import ChatContent from "./ChatContent";
import { useDraggableFixed } from "../../hooks/useDraggableFixed";
import { StartAgent } from "../../modules/tclip/tasks/helpers.tsx";
import { useNavigate } from "@tanstack/react-router";
import useInterval from "../../hooks/useInterval";
import { AGENT_STATUS_COLOR } from "../../modules/tclip/tasks/helpers";
import { AdDayPartingToolMessage } from "./AdBulkActions/AdDayPartingToolMessage";
import { AdStatusToolMessage } from "./AdBulkActions/AdStatusToolMessage";
import { AdBulkUpdateToolMessage } from "./AdBulkActions/AdBulkUpdateToolMessage";
import { AdScheduleBudgetToolMessage } from "./AdBulkActions/AdScheduleBudgetToolMessage";
import {
  changeAdStatusTool,
  scheduleBudgetTool,
  setAdvertisementBulkUpdateTool,
  setAdvertisementDaypartingTool,
} from "./AdBulkActions/tools";
export const Button = styled("button", {
  base: {
    backgroundColor: "fuchsia",
    color: "white",
    border: "none",
    borderRadius: "5px",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "opacity 0.2s ease",
    _disabled: {
      opacity: 0.65,
      cursor: "not-allowed",
    },
  },
});

const AnalysisWorkflow = ({ title, prompt, messageId, agentId }) => {
  const [isError, setIsError] = useState(false);
  const [agent, setAgent] = useState(null);
  const [incommingAgentId, setIncommingAgentId] = useState(agentId);
  const marketplace = useAppSelector((state) => state.marketPlace.marketPlace);
  const navigate = useNavigate();
  const { t } = useTranslation(["chat", "tclip"]);

  const GetAgent = async () => {
    try {
      const res = await api.get(`/clio/tasks/definitions/${incommingAgentId}/`);
      setAgent(res.data);
      setIncommingAgentId(res.data.id);
      await UpdateMessage(res.data.id);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setIsError(true);
      }
    }
  };
  const LaunchGetAgent = async () => {
    await GetAgent();
  };

  const ExectuteAgent = async () => {
    const res = await StartAgent(title, prompt, marketplace, null, true, false);
    setAgent(res.data);
    setIncommingAgentId(res.data.id);
    await UpdateMessage(res.data.id);
  };

  const UpdateMessage = async (agentId) => {
    await api.patch("/clio/chat", { agent_id: agentId, message_id: messageId });
  };

  useEffect(() => {
    if (incommingAgentId) {
      LaunchGetAgent();
    } else {
      ExectuteAgent();
    }
    return () => {};

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId]);

  const handleViewAgents = () => {
    localStorage.setItem("redirectKey", agent.clio_report_id);
    navigate({ to: "/user/tclip/playground" });
  };

  const message = useMemo(() => {
    if (isError) return t(($) => $.chat["Agent not found or has been archived"]);
    if (!agent && agentId) return t(($) => $.chat["Retrieving agent status..."]);
    if (!agent) return t(($) => $.chat["Answering your query, this may take a few minutes..."]);

    if (agent.status === "ready") return "ready";

    const statusKey = AGENT_STATUS_COLOR[agent.status].label;
    return t(($) => $.chat["Agent Status: {status}"], {
      status: t(($) => $.tclip.agentStatus[statusKey]),
    });
  }, [agent, isError, agentId, t]);

  useInterval(
    async () => {
      GetAgent();
    },
    isError || !agent || message.toLowerCase() === "ready" ? null : 5000,
  );

  return (
    <>
      {agent && agent.status === "ready" && (
        <Button onClick={handleViewAgents}>{t(($) => $.chat["View Agent"])}</Button>
      )}
      {(!agent || agent.status !== "ready") && (
        <>
          <div
            className={`${css({
              fontSize: "14px",
              color: "black",
              padding: "8px",
              borderRadius: "5px",
              marginBottom: "0px",
              width: "100%",
              alignSelf: "flex-start",
            })} chat-markdown`}
          >
            {t(
              ($) =>
                $.chat[
                  "Your question could not be answered directly, so we have started an analysis workflow to assist you."
                ],
            )}{" "}
            {/* {loading && <Loading />} */}
          </div>
          <div
            className={`${css({
              fontSize: "14px",
              color: "black",
              padding: "8px",
              borderRadius: "5px",
              marginBottom: "10px",
              width: "100%",
              alignSelf: "flex-start",
            })} chat-markdown`}
          >
            <Markdown>{message}</Markdown>
          </div>
        </>
      )}
    </>
  );
};

const BotMessage = ({ text, message }) => {
  const { t } = useTranslation(["chat"]);
  if (message?.meta?.function_call) {
    const messageId = message.id;
    const agentId = message.meta.agent_id;
    const functionCall = message.meta.function_call;
    const functionName = functionCall.name;
    const functionArgs = functionCall.arguments || "{}";
    if (functionName === "change_ad_status") {
      const { advertisement_ids, ad_ids, state } = JSON.parse(functionArgs);
      const ids = advertisement_ids ?? ad_ids ?? [];
      return <AdStatusToolMessage adIds={ids} state={state} />;
    }
    if (functionName === "set_advertisement_dayparting") {
      const { advertisement_ids, ad_ids, day_parting_multipliers, day_parting_method } = JSON.parse(functionArgs);
      const ids = advertisement_ids ?? ad_ids ?? [];
      return (
        <AdDayPartingToolMessage
          adIds={ids}
          dayPartingMultipliers={day_parting_multipliers}
          dayPartingMethod={day_parting_method}
        />
      );
    }
    if (functionName === "set_advertisement_bulk_update") {
      const { advertisement_ids, ad_ids, updates } = JSON.parse(functionArgs);
      const ids = advertisement_ids ?? ad_ids ?? [];
      return <AdBulkUpdateToolMessage adIds={ids} updates={updates} />;
    }
    if (functionName === "schedule_budget_tool") {
      const parsedArgs = JSON.parse(functionArgs);
      const { advertisement_ids, ad_ids, ...rest } = parsedArgs;
      const ids = advertisement_ids ?? ad_ids ?? [];
      return <AdScheduleBudgetToolMessage adIds={ids} payload={rest} />;
    }
    if (functionName === "analysis") {
      const { title, user_query } = JSON.parse(functionArgs);
      return <AnalysisWorkflow title={title} prompt={user_query} messageId={messageId} agentId={agentId} />;
    }
    return (
      <div
        className={css({
          fontSize: "14px",
          color: "black",
          padding: "8px",
          borderRadius: "5px",
          marginBottom: "10px",
          borderColor: "fuchsia",
          width: "100%",
          alignSelf: "flex-start",
          backgroundColor: "#f0f0f0",
        })}
      >
        <strong>{t(($) => $.chat["Function Call:"])}</strong> {functionName}
        <pre className={css({ whiteSpace: "pre-wrap", margin: "5px 0" })}>{functionArgs}</pre>
      </div>
    );
  }
  return (
    <div
      className={`${css({
        fontSize: "14px",
        color: "black",
        padding: "8px",
        borderRadius: "5px",
        marginBottom: "10px",
        width: "100%",
        alignSelf: "flex-start",
      })} chat-markdown`}
    >
      <Markdown
        components={{
          a: ({ node, ...props }) => {
            const href = props.href || "";
            const isInternalLink = href.startsWith(window.location.origin) || href.startsWith("/");
            if (isInternalLink) {
              return <TrellisLink to={href.replace(window.location.origin, "")}>{props.children}</TrellisLink>;
            }
            return (
              <a href={href} target="_blank" rel="noopener noreferrer">
                {props.children}
              </a>
            );
          },
        }}
      >
        {text}
      </Markdown>
    </div>
  );
};

const analysisTool = () => {
  return {
    name: "analysis",
    description:
      "Special workflow tool for handling complex or large analytics queries. " +
      "Use this when the user's question is about advertising or sales data but would produce too much data, for a direct MCP call, or requires multi-step reasoning. " +
      "Examples include: grouping metrics by day over many months, combining multiple ASINs or campaigns, " +
      "or when the query spans multiple rollup tables. " +
      "Use MCP tools (like get_stats) for simple, direct lookups. " +
      "If the query requires complex calculations or data manipulation, use this tool. " +
      "If answering the query directly with MCP tools is not possible or would be very difficult, use this tool instead. " +
      "Do not use this for visualization (reports or widgets). " +
      "Examples: 'sales and spend by ASIN per day for the last 6 months', " +
      "Example: 'ads that are serving, having ACOS > 0.5, sales < 50000, monthly budget < 10000 in last 6 months, " +
      "and show bid amounts for products in these ads by each day'.",
    parameters: {
      type: "object",
      properties: {
        title: {
          type: "string",
          description: "The title of the analysis",
          default: "Analysis Workflow",
        },
        prompt: {
          type: "string",
          description: "The prompt for the analysis",
          default: "Please provide your analysis.",
        },
        user_query: {
          type: "string",
          description: "The user's query for the analysis",
          default: "Please provide your query.",
        },
      },
      required: ["title", "prompt", "user_query"],
    },
  };
};

const Chat = () => {
  const { t } = useTranslation(["common", "chat"]);
  const [tab, setTab] = useState("chat");
  const [isOpen, setIsOpen] = useState(false);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [message, setMessage] = useState("");
  const [pendingMessage, setPendingMessage] = useState("");
  const state = useAppSelector((state) => state);
  const { marketPlace } = state.marketPlace;
  const user_id = state.user.user_id;
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const chatContext = useContext(ChatContext);
  const { currentContext, isFullScreen, setIsFullScreen } = chatContext;
  const { panelRef, panelStyle, getHeaderBind } = useDraggableFixed({
    initial: { x: window.innerWidth - 468, y: 132 },
    margin: 12,
    storageKey: "chat-position",
  });

  const headerBind = getHeaderBind();

  const { data: conversationData } = useFetch(
    ["clio-conversation", marketPlace],
    "/clio/conversations/",
    {
      market_place: marketPlace,
    },
    {
      retry: false,
      keepPreviousData: true,
      refetchInterval: 10000,
    },
  );

  const { data: messageData } = useFetch(
    ["clio-chat", marketPlace, currentConversation],
    "/clio/chat/",
    {
      market_place: marketPlace,
      conversation: currentConversation,
    },
    {
      retry: false,
      keepPreviousData: true,
    },
  );

  const conversation = useMemo(() => {
    if (!conversationData) return null;
    return conversationData.data.results.find((conv) => conv.external_id === currentConversation);
  }, [conversationData, currentConversation]);

  const messages = useMemo(() => {
    if (!messageData) return [];
    return messageData.data.results;
  }, [messageData]);

  const client = useQueryClient();

  useEffect(() => {
    // clear the pending message if messages change
    if (messages.some((msg) => msg.content === pendingMessage)) {
      setPendingMessage("");
    }
  }, [messages, pendingMessage]);

  const sendMessageMutation = useMutation({
    mutationFn: async (newMessage, title) => {
      // set interval to invalidate queries
      const handle = setInterval(() => {
        client.invalidateQueries({ queryKey: ["clio-chat"] });
      }, 3000);
      const baseUrl = "";
      const linking = `
        Always include links to pages using entity ids (ad_id, product_id, category_id) when listing entities. Here are some useful links, don't add additonal query params to the urls, you can link to products on amazon or walmart as well:\n
        Current Link: ${window.location.href}\n
        Deep links:\n
        Main Dashboard, ${baseUrl}/user/dashboard\n
        Category Dashboard, ${baseUrl}/user/merchandising/category/{category_id}\n
        Product Dashboard, ${baseUrl}/user/merchandising/product/{product_id}\n
        Ads Dashboard, ${baseUrl}/user/advertising/dashboard\n
        Ads Category Dashboard, ${baseUrl}/user/advertising/dashboard/category/{category_id}\n
        Ad Dashboard, ${baseUrl}/user/advertising/dashboard/ads/{ad_id}\n
        Ads Report, ${baseUrl}/user/report/advertisement\n
        Target Report, ${baseUrl}/user/report/target\n
        Search Term Report, ${baseUrl}/user/report/str_report\n
        Products Report, ${baseUrl}/user/report/product\n
        AMC Dashboard, ${baseUrl}/user/amc/dashboard\n
        Goals Dashboard, ${baseUrl}/user/goals\n
        Dynamic Pricing Dashboard, ${baseUrl}/user/pricing/dashboard\n\n
      `;
      const body = {
        market_place: marketPlace,
        user_id: user_id,
        context: `You are an ecommerce analyst helping a seller manage their advertising and pricing on ${channel} ${marketPlace} using the Trellis platform.${linking}\n${currentContext ? " Additional context to answer questions: " + currentContext : ""}`,
        tools: [
          changeAdStatusTool(),
          analysisTool(),
          setAdvertisementDaypartingTool(),
          setAdvertisementBulkUpdateTool(),
          scheduleBudgetTool(),
        ],
      };
      if (currentConversation) {
        body.conversation = currentConversation;
      } else {
        const conversationResponse = await api.post("/clio/conversations/", {
          market_place: marketPlace,
          user_id: user_id,
          title: title || `New Conversation ${new Date().toLocaleString()}`,
        });
        body.conversation = conversationResponse.data.external_id;
        setCurrentConversation(conversationResponse.data.external_id);
        client.invalidateQueries({ queryKey: ["clio-conversation"] });
      }
      return await api
        .post("/clio/chat/", {
          ...body,
          ...newMessage,
        })
        .finally(() => {
          clearInterval(handle);
        });
    },
    onSuccess: (res) => {
      const newMessage = res.data;
      if (!currentConversation) {
        setCurrentConversation(newMessage.conversation);
        client.invalidateQueries({ queryKey: ["clio-conversation"] });
      }
      client.invalidateQueries({ queryKey: ["clio-chat"] });
    },
    onError: (error) => {
      client.invalidateQueries({ queryKey: ["clio-chat"] });

      // oxlint-disable-next-line no-console
      console.error("Error sending message:", error);
      setMessage(pendingMessage);
      setPendingMessage("");
    },
  });

  const handleSendMessage = () => {
    if (message.trim()) {
      setPendingMessage(message.trim());
      setMessage("");
      sendMessageMutation.mutate({
        content: message.trim(),
      });
    }
  };

  const handleStartNewConversation = (message, title) => {
    setCurrentConversation(null);
    setMessage("");
    setPendingMessage("");
    client.invalidateQueries({ queryKey: ["clio-chat"] });
    setTab("chat");
    sendMessageMutation.mutate(message, title);
  };

  // Render full screen chat if enabled
  if (isFullScreen) {
    return (
      <FullScreenChat
        onStartNewConversation={handleStartNewConversation}
        currentConversation={currentConversation}
        setCurrentConversation={setCurrentConversation}
        conversationData={conversationData}
        client={client}
        setIsOpen={setIsOpen}
      >
        <ChatContent
          currentConversation={currentConversation}
          messages={messages}
          sendMessageMutation={sendMessageMutation}
          pendingMessage={pendingMessage}
          message={message}
          setMessage={setMessage}
          handleSendMessage={handleSendMessage}
          BotMessage={BotMessage}
        />
      </FullScreenChat>
    );
  }

  return (
    <>
      <button
        title={t(($) => $.chat["Ask Trellis (Alpha)"])}
        className={css({
          position: "relative",
          fontSize: "small",
          lineHeight: "0",
          fontWeight: "bold",
          background: "linear-gradient(145deg,rgba(213, 0, 163, 1) 0%, rgba(98, 63, 127, 1) 100%)",
          color: "white",
          //borderRadius: "50%",
          borderRadius: "12px",
          //width: "40px",
          height: "36px",
          border: "none",
          padding: "8px 14px 8px 12px",
          letterSpacing: "0.5px",
          cursor: "pointer",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
          zIndex: "overlay",
          _before: {
            content: "''",
            position: "absolute",
            //borderRadius: "50%",
            borderRadius: "12px",
            inset: "-2px" /* control the spread */,
            transform: "translate(1px, 1px), scale(0)" /* control the offsets */,
            zIndex: "-1" /* place the element behind */,
            background:
              "linear-gradient(145deg,rgba(213, 0, 163, 1) 0%, rgba(98, 63, 127, 1) 100%)" /* your gradient here */,
            filter: "blur(7px)",
            animation: "linear rotatePulse 1s 20s",
            opacity: 0.7,
          },
          _hover: {
            _before: {
              animation: "linear rotatePulse 1s infinite",
            },
          },
        })}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={css({ fontSize: "18px", marginRight: "3px" })}>
          <HiOutlineLightBulb />
        </span>
        {t(($) => $.chat["Ask Trellis"])}
        <span
          className={css({
            marginLeft: "6px",
            fontSize: "8px",
            fontWeight: "600",
            backgroundColor: "rgba(255, 255, 255, 0.25)",
            padding: "2px 5px",
            borderRadius: "8px",
            textTransform: "uppercase",
            letterSpacing: "0.3px",
          })}
        >
          {t(($) => $.chat["Alpha"])}
        </span>
      </button>
      {isOpen && (
        <div
          className={css({
            position: "fixed",
            background: "white",
            zIndex: "overlay",
            borderRadius: "10px",
            right: "8px",
            top: "132px",
            border: "1px solid #ddd",
            height: "650px",
            width: "460px",
            boxShadow: "card",
            overflow: "hidden", // Ensure content respects border radius
          })}
          style={panelStyle}
          ref={panelRef}
        >
          <VStack height="full" width="full" gap="0">
            {tab === "menu" ?
              <>
                <div
                  className={css({
                    display: "flex",
                    alignItems: "center",
                    padding: "10px",
                    borderBottom: "1px solid #ddd",
                    backgroundColor: "white",
                    minHeight: "50px",
                    width: "100%",
                  })}
                  {...headerBind}
                >
                  <div className={css({ fontSize: "16px", fontWeight: "bold", flexGrow: 1 })}>
                    {t(($) => $.chat["Conversations"])}
                  </div>
                  <button
                    className={css({
                      backgroundColor: "white",
                      fontSize: "18px",
                      color: "black",
                      borderRadius: "4px",
                      width: "30px",
                      height: "30px",
                      border: "none",
                      cursor: "pointer",
                      marginRight: "8px",
                      _hover: { backgroundColor: "#f0f0f0" },
                    })}
                    onClick={() => setIsFullScreen(true)}
                    title={t(($) => $.chat["Full Screen"])}
                  >
                    <MdFullscreen />
                  </button>
                  <button
                    className={css({
                      backgroundColor: "white",
                      fontSize: "18px",
                      color: "black",
                      borderRadius: "4px",
                      width: "30px",
                      height: "30px",
                      border: "none",
                      cursor: "pointer",
                      _hover: { backgroundColor: "#f0f0f0" },
                    })}
                    onClick={() => setIsOpen(false)}
                    title={t(($) => $.common.actions["Close"])}
                  >
                    <HiMinus />
                  </button>
                </div>
                <ConversationsSidebar
                  onStartNewConversation={handleStartNewConversation}
                  currentConversation={currentConversation}
                  setCurrentConversation={setCurrentConversation}
                  conversationData={conversationData}
                  client={client}
                  setTab={setTab}
                  isFullScreen={false}
                />
              </>
            : <>
                <div
                  className={css({
                    display: "flex",
                    alignItems: "center",
                    padding: "10px",
                    borderBottom: "1px solid #ddd",
                    backgroundColor: "white",
                    minHeight: "50px",
                    width: "100%",
                  })}
                  {...headerBind}
                >
                  <button
                    className={css({
                      backgroundColor: "white",
                      fontSize: "18px",
                      color: "black",
                      borderRadius: "4px",
                      width: "30px",
                      height: "30px",
                      border: "none",
                      cursor: "pointer",
                      marginRight: "8px",
                      _hover: { backgroundColor: "#f0f0f0" },
                    })}
                    onClick={() => setTab("menu")}
                    title={t(($) => $.chat["Conversations"])}
                  >
                    <HiMenu />
                  </button>
                  <div
                    className={css({
                      fontSize: "14px",
                      fontWeight: "bold",
                      flexGrow: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0, // Important for text truncation in flex containers
                    })}
                  >
                    {conversation?.title || t(($) => $.chat["New Conversation"])}
                  </div>
                  <button
                    className={css({
                      backgroundColor: "white",
                      fontSize: "18px",
                      color: "black",
                      borderRadius: "4px",
                      width: "30px",
                      height: "30px",
                      border: "none",
                      cursor: "pointer",
                      marginRight: "4px",
                      _hover: { backgroundColor: "#f0f0f0" },
                    })}
                    onClick={() => setCurrentConversation(null)}
                    title={t(($) => $.chat["New Conversation"])}
                  >
                    <HiPencilAlt />
                  </button>
                  <button
                    className={css({
                      backgroundColor: "white",
                      fontSize: "18px",
                      color: "black",
                      borderRadius: "4px",
                      width: "30px",
                      height: "30px",
                      border: "none",
                      cursor: "pointer",
                      marginRight: "4px",
                      _hover: { backgroundColor: "#f0f0f0" },
                    })}
                    onClick={() => setIsFullScreen(true)}
                    title={t(($) => $.chat["Full Screen"])}
                  >
                    <MdFullscreen />
                  </button>
                  <button
                    className={css({
                      backgroundColor: "white",
                      fontSize: "18px",
                      color: "black",
                      borderRadius: "4px",
                      width: "30px",
                      height: "30px",
                      border: "none",
                      cursor: "pointer",
                      _hover: { backgroundColor: "#f0f0f0" },
                    })}
                    onClick={() => setIsOpen(false)}
                    title={t(($) => $.common.actions["Close"])}
                  >
                    <HiMinus />
                  </button>
                </div>
                <ChatContent
                  currentConversation={currentConversation}
                  messages={messages}
                  sendMessageMutation={sendMessageMutation}
                  pendingMessage={pendingMessage}
                  message={message}
                  setMessage={setMessage}
                  handleSendMessage={handleSendMessage}
                  BotMessage={BotMessage}
                />
              </>
            }
            {/* Menu tab for conversations or Chat tab - handled by conditional rendering above */}
          </VStack>
        </div>
      )}
    </>
  );
};

export default Chat;
